import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import Layout from '../Layout'
import { getReconciliationReportApi, getAllocationReadinessApi } from '../../../apis/apis'
import type {
    ReconciliationReportVO,
    ReconciliationViewPreset,
    ReconciliationIssueType,
    ReconciliationRowVO,
    ReconciliationFieldVO,
    AllocationReadinessResult,
    AllocationReadinessItem,
} from '../../../apis/types'
import ReconciliationSummary from './ReconciliationSummary'
import ReconciliationFilters from './ReconciliationFilters'
import ReconciliationTable from './ReconciliationTable'
import ReconciliationLegend from './ReconciliationLegend'
import ReconciliationActionChecklist from './ReconciliationActionChecklist'
import PortalOnlyAccountsTable from './PortalOnlyAccountsTable'
import HiddenAccountsPanel from './HiddenAccountsPanel'
import ReconciliationVnemPanel from './ReconciliationVnemPanel'
import type { MissingVnemAccount } from './ReconciliationVnemPanel'
import { downloadCsv } from './exportCsv'
import { fieldColumns, displayString } from './reconciliation.types'

function sfUnavailableField(): ReconciliationFieldVO {
    return {
        authoritativeSource: 'salesforce',
        status: 'sourceUnavailable',
        values: {},
        display: null,
    }
}

function sfField(display: string | number | boolean | null): ReconciliationFieldVO {
    return {
        authoritativeSource: 'salesforce',
        status: display != null ? 'exact' : 'missing',
        values: { salesforce: display },
        display,
    }
}

function enrichRowsWithReadiness(
    rows: ReconciliationRowVO[],
    readiness: AllocationReadinessResult | null,
): ReconciliationRowVO[] {
    const lookup = new Map<string, AllocationReadinessItem>()
    if (readiness) {
        for (const item of readiness.allocations) {
            const meter = item.allocation.meterNumber
            if (meter) lookup.set(meter, item)
        }
    }

    return rows.map((row) => {
        const match = row.meterNumber ? lookup.get(row.meterNumber) : undefined
        if (!match) {
            return {
                ...row,
                fields: {
                    ...row.fields,
                    pmaApproved: sfUnavailableField(),
                    onActiveTable: sfUnavailableField(),
                    vaTableStatus: sfUnavailableField(),
                },
            }
        }
        return {
            ...row,
            fields: {
                ...row.fields,
                pmaApproved: sfField(match.allocation.pmaApproved),
                onActiveTable: sfField(match.allocation.onActiveTable),
                vaTableStatus: sfField(match.allocation.vaTableStatus),
            },
        }
    })
}

function filterRows(
    rows: ReconciliationRowVO[],
    view: ReconciliationViewPreset,
    issueType: ReconciliationIssueType | undefined,
): ReconciliationRowVO[] {
    let filtered = rows

    if (issueType) {
        return filtered.filter((r) => r.issues.includes(issueType))
    }

    switch (view) {
        case 'issuesOnly':
            return filtered.filter((r) => r.issues.length > 0)
        case 'fullyReconciled':
            return filtered.filter((r) => r.issues.length === 0)
        case 'mismatches':
            return filtered.filter((r) =>
                fieldColumns.some((col) => r.fields[col.fieldKey].status === 'mismatch'),
            )
        default:
            return filtered
    }
}

const ReconciliationReport: React.FC = () => {
    const { programId } = useParams<{ programId: string }>()
    const [report, setReport] = useState<ReconciliationReportVO | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [activeView, setActiveView] = useState<ReconciliationViewPreset>('all')
    const [activeIssueType, setActiveIssueType] = useState<ReconciliationIssueType | undefined>(undefined)

    const fetchReport = useCallback(async () => {
        if (!programId) return
        setIsLoading(true)
        setError(null)
        try {
            const [data, readiness] = await Promise.all([
                getReconciliationReportApi(programId),
                getAllocationReadinessApi(programId).catch((err) => {
                    console.warn('[Reconciliation] Readiness API failed, SF columns will be unavailable:', err)
                    return null
                }),
            ])
            data.rows = enrichRowsWithReadiness(data.rows, readiness)
            setReport(data)
        } catch (err) {
            setError('Failed to load reconciliation report.')
            console.error(err)
        } finally {
            setIsLoading(false)
        }
    }, [programId])

    useEffect(() => {
        void fetchReport()
    }, [fetchReport])

    const filteredRows = useMemo(
        () => (report ? filterRows(report.rows, activeView, activeIssueType) : []),
        [report, activeView, activeIssueType],
    )

    const handleExport = () => {
        if (!report) return
        downloadCsv(report.rows, report.property.propertyName)
    }

    const handleRefresh = () => {
        void fetchReport()
    }

    const handleIssueClick = (issueType: ReconciliationIssueType) => {
        setActiveIssueType(issueType)
        setActiveView('all')
    }

    const activeIssueLabel = activeIssueType
        ? report?.uiHints?.issueTypes?.[activeIssueType]?.label ?? activeIssueType
        : undefined

    const missingVnemAccounts: MissingVnemAccount[] = useMemo(
        () =>
            report
                ? report.rows
                      .filter((r) => r.fields.vnemPct.status === 'missing')
                      .map((r) => ({ name: displayString(r.fields.accountName) ?? '', utilityAccountNumber: r.utilityAccountNumber }))
                : [],
        [report],
    )

    return (
        <Layout title="Property Data Reconciliation">
            <div>
                {/* Breadcrumb */}
                <nav className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                    <Link to="/program/programs" className="hover:text-indigo-700 dark:hover:text-indigo-400">Programs</Link>
                    <span className="mx-1">/</span>
                    <Link to={`/program/programs/${programId}`} className="hover:text-indigo-700 dark:hover:text-indigo-400">
                        {report?.property.propertyName ?? 'Program'}
                    </Link>
                    <span className="mx-1">/</span>
                    <span className="text-slate-900 dark:text-white font-medium">Reconciliation</span>
                </nav>

                {/* Page Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-medium text-indigo-900 dark:text-white">Property Data Reconciliation</h1>
                        {report && (
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                {report.property.propertyName}
                                {report.property.utilityShortName && <> &middot; {report.property.utilityShortName}</>}
                                {report.summary && <> &middot; {report.summary.totalRows} utility accounts</>}
                                {report.generatedAt && <> &middot; Data queried {new Date(report.generatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        {report && report.rows.length > 0 && (
                            <button
                                onClick={handleExport}
                                className="inline-flex items-center px-4 py-2 border border-slate-300 dark:border-slate-600 text-sm font-medium rounded-md text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700"
                            >
                                <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                                Export CSV
                            </button>
                        )}
                        <button
                            onClick={handleRefresh}
                            disabled={isLoading}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                        >
                            <ArrowPathIcon className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                            Refresh Data
                        </button>
                    </div>
                </div>

                {isLoading && !report && <div className="text-sm font-medium dark:text-white">Loading...</div>}

                {error && (
                    <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4 mb-4">
                        <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
                    </div>
                )}

                {report && (
                    <>
                        {report.summary && (
                            <ReconciliationSummary
                                summary={report.summary}
                                dataCoverage={report.dataCoverage}
                                property={report.property}
                                issueCountsByType={report.issueCountsByType}
                                uiHints={report.uiHints}
                                onIssueClick={handleIssueClick}
                            />
                        )}

                        <ReconciliationFilters
                            activeView={activeView}
                            onViewChange={setActiveView}
                            activeIssueType={activeIssueType}
                            activeIssueLabel={activeIssueLabel}
                            onIssueTypeChange={setActiveIssueType}
                            summary={report.summary}
                            filteredCount={filteredRows.length}
                        />

                        <ReconciliationTable
                            rows={filteredRows}
                            totalRows={report.summary?.totalRows ?? report.rows.length}
                            utilityShortName={report.property.utilityShortName}
                        />

                        <ReconciliationLegend />

                        {report.dataLimitations && report.dataLimitations.length > 0 && (
                            <div className="mt-4 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3">
                                <p className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-1">Data Limitations</p>
                                <ul className="list-disc list-inside space-y-0.5">
                                    {report.dataLimitations.map((msg, i) => (
                                        <li key={i} className="text-xs text-amber-700 dark:text-amber-300">{msg}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <ReconciliationActionChecklist rows={filteredRows} />

                        {report.portalOnlyAccounts && report.portalOnlyAccounts.length > 0 && (
                            <PortalOnlyAccountsTable accounts={report.portalOnlyAccounts} />
                        )}
                        {report.hiddenAccounts && report.hiddenAccounts.length > 0 && (
                            <HiddenAccountsPanel accounts={report.hiddenAccounts} />
                        )}

                        {report.summary && (
                            <ReconciliationVnemPanel
                                vnem={report.summary.vnemAllocation}
                                missingAccounts={missingVnemAccounts}
                            />
                        )}
                    </>
                )}
            </div>
        </Layout>
    )
}

export default ReconciliationReport
